import React, { useCallback, useEffect, useRef, useState } from "react"
import { motion, useReducedMotion } from "framer-motion"
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react"

type SectionLink = {
  href: string
  label: string
}

type Props = {
  links: SectionLink[]
}

const sectionIdFromHref = (href: string) => href.replace(/^#/, "")

const ChapterSectionNav: React.FC<Props> = ({ links }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const linkRefs = useRef<Map<string, HTMLAnchorElement>>(new Map())
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [activeSection, setActiveSection] = useState(() => sectionIdFromHref(links[0]?.href ?? ""))
  const prefersReducedMotion = useReducedMotion()

  const updateScrollState = useCallback(() => {
    const element = scrollRef.current
    if (!element) return

    setCanScrollLeft(element.scrollLeft > 8)
    setCanScrollRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 8)
  }, [])

  useEffect(() => {
    updateScrollState()
    const element = scrollRef.current
    if (!element) return

    element.addEventListener("scroll", updateScrollState, { passive: true })
    window.addEventListener("resize", updateScrollState)

    return () => {
      element.removeEventListener("scroll", updateScrollState)
      window.removeEventListener("resize", updateScrollState)
    }
  }, [updateScrollState, links])

  useEffect(() => {
    const sectionIds = links.map((link) => sectionIdFromHref(link.href))
    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null)

    if (elements.length === 0) return

    const visibleSections = new Map<string, number>()

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id
          if (entry.isIntersecting) {
            visibleSections.set(id, entry.intersectionRatio)
          } else {
            visibleSections.delete(id)
          }
        })

        if (visibleSections.size === 0) return

        const nextActive = [...visibleSections.entries()].sort((a, b) => b[1] - a[1])[0]?.[0]
        if (nextActive) setActiveSection(nextActive)
      },
      {
        rootMargin: "-25% 0px -55% 0px",
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      }
    )

    elements.forEach((element) => observer.observe(element))

    return () => observer.disconnect()
  }, [links])

  useEffect(() => {
    const activeLink = linkRefs.current.get(activeSection)
    activeLink?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
  }, [activeSection])

  const scrollBy = (direction: -1 | 1) => {
    scrollRef.current?.scrollBy({ left: direction * 220, behavior: "smooth" })
  }

  return (
    <nav
      aria-label="Chapter sections"
      className="sticky top-[52px] z-40 border-b border-[#ebebeb] bg-white/90 backdrop-blur-md md:top-[60px]"
    >
      <div className="relative mx-auto w-full max-w-6xl">
        {canScrollLeft && (
          <>
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-14 bg-gradient-to-r from-white/95 to-transparent md:w-20" />
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="absolute left-2 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-[#ebebeb] bg-white text-[#5a5a5a] shadow-sm transition hover:border-dc-primary-900/20 hover:text-dc-primary-900 md:left-4"
              aria-label="Scroll sections left"
            >
              <IconChevronLeft size={16} />
            </button>
          </>
        )}

        {canScrollRight && (
          <>
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-14 bg-gradient-to-l from-white/95 to-transparent md:w-20" />
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="absolute right-2 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-[#ebebeb] bg-white text-[#5a5a5a] shadow-sm transition hover:border-dc-primary-900/20 hover:text-dc-primary-900 md:right-4"
              aria-label="Scroll sections right"
            >
              <IconChevronRight size={16} />
            </button>
            <div
              className="pointer-events-none absolute bottom-1 right-6 z-20 flex items-center gap-0.5 text-[#9e7e43] md:right-10"
              aria-hidden
            >
              <span className="font-dm-sans text-[10px] font-semibold uppercase tracking-wider">Scroll</span>
              <IconChevronRight size={12} className="animate-pulse" />
            </div>
          </>
        )}

        <div
          ref={scrollRef}
          className="flex gap-1 overflow-x-auto px-4 py-2.5 [-ms-overflow-style:none] [scrollbar-width:none] md:px-8 lg:px-20 [&::-webkit-scrollbar]:hidden"
        >
          {links.map((link) => {
            const sectionId = sectionIdFromHref(link.href)
            const isActive = activeSection === sectionId

            return (
              <a
                key={link.href}
                ref={(node) => {
                  if (node) linkRefs.current.set(sectionId, node)
                  else linkRefs.current.delete(sectionId)
                }}
                href={link.href}
                aria-current={isActive ? "location" : undefined}
                className={[
                  "relative shrink-0 rounded-full px-3.5 py-1.5 font-dm-sans text-xs font-medium transition-colors duration-300 md:text-sm",
                  isActive
                    ? "font-semibold text-dc-primary-900"
                    : "text-[#5a5a5a] hover:text-dc-primary-900",
                ].join(" ")}
              >
                {isActive && (
                  <motion.span
                    layoutId="chapter-section-nav-active"
                    className="absolute inset-0 rounded-full bg-[#f4f7ff]"
                    transition={
                      prefersReducedMotion
                        ? { duration: 0 }
                        : { type: "spring", stiffness: 380, damping: 32, mass: 0.8 }
                    }
                  />
                )}
                <span className="relative z-10">{link.label}</span>
              </a>
            )
          })}
        </div>
      </div>
    </nav>
  )
}

export default ChapterSectionNav
